import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { leasesAPI, tenantsAPI, apartmentsAPI, propertiesAPI, usersAPI, fileViewUrl } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { Plus, Search, AlertTriangle, RefreshCw, Edit2, Trash2, Paperclip, FileText } from 'lucide-react';
import { fmt, statusLabel, statusClass, daysUntil, errMsg } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const EMPTY = { apartmentId:'', tenantId:'', startDate:'', endDate:'', monthlyRent:'', rentDueDay:'1', securityDeposit:'', petDeposit:'0', status:'active', autoRenew:false, parkingIncluded:false, leaseType:'Annual', utilitiesIncluded:'', signedDate:'' };

export default function Leases() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { can }  = usePermissions();
  const isStaff   = user?.role === 'admin' || user?.role === 'manager'; // for data loading
  const canCreate = can('leases', 'create');
  const canEdit   = can('leases', 'edit');
  const canDelete = can('leases', 'delete');

  const [leases, setLeases]   = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [apts, setApts]       = useState([]);
  const [props, setProps]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [statusF, setStatusF] = useState('');
  const [modal, setModal]         = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [detail, setDetail]   = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [editForm, setEditForm] = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [file,     setFile]     = useState(null);
  const [editFile, setEditFile] = useState(null);
  const fileRef     = useRef();
  const editFileRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    const q = {}; if (statusF) q.status = statusF;
    const empty = Promise.resolve({ data: { data: [] } });
    // Tenants get 403 on properties/apartments endpoints — skip those calls
    const calls = isStaff
      ? [leasesAPI.getAll(q), tenantsAPI.getAll(), usersAPI.getAll({ role: 'tenant' }), apartmentsAPI.getAll(), propertiesAPI.getAll()]
      : [leasesAPI.getAll(q), empty, empty, empty, empty];
    Promise.all(calls)
      .then(([l,tn,tu,a,p])=>{ setLeases(l.data.data); setTenants(tn.data.data); setTenantUsers(tu.data.data||[]); setApts(a.data.data); setProps(p.data.data); })
      .catch(()=>toast.error(t('failedToLoad')))
      .finally(()=>setLoading(false));
  }, [statusF, t, isStaff]);

  useEffect(()=>{ load(); },[load]);

  const filtered = leases.filter(l => !search || (l.tenantId?.name||l.userId?.name||'').toLowerCase().includes(search.toLowerCase()));
  const expiring = leases.filter(l => { const d = daysUntil(l.endDate); return d!==null&&d>=0&&d<=60&&l.status==='active'; });

  const buildFd = (formData, fileObj) => {
    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (k === 'utilitiesIncluded') return;
      if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
    });
    const utils = formData.utilitiesIncluded ? formData.utilitiesIncluded.split(',').map(s => s.trim()).filter(Boolean) : [];
    utils.forEach(u => fd.append('utilitiesIncluded', u));
    if (fileObj) fd.append('leaseFile', fileObj);
    return fd;
  };

  const handleSave = async () => {
    if(!form.monthlyRent) return toast.error(t('apartmentTenantRentRequired'));
    setSaving(true);
    try {
      await leasesAPI.create(buildFd(form, file));
      toast.success(t('leaseCreated')); setModal(false); setForm(EMPTY); setFile(null); load();
    } catch(err){ toast.error(errMsg(err)); }
    finally{ setSaving(false); }
  };

  const openEdit = (lease) => {
    setEditId(lease._id);
    setEditForm({
      apartmentId: lease.apartmentId?._id || lease.apartmentId || '',
      tenantId: lease.tenantId?._id || lease.userId?._id || '',
      startDate: lease.startDate ? lease.startDate.slice(0,10) : '',
      endDate: lease.endDate ? lease.endDate.slice(0,10) : '',
      monthlyRent: lease.monthlyRent || '',
      rentDueDay: String(lease.rentDueDay || 1),
      securityDeposit: lease.securityDeposit || '',
      petDeposit: lease.petDeposit || '0',
      status: lease.status || 'active',
      autoRenew: lease.autoRenew || false,
      parkingIncluded: lease.parkingIncluded || false,
      leaseType: lease.leaseType || 'Annual',
      utilitiesIncluded: (lease.utilitiesIncluded||[]).join(', '),
      signedDate: lease.signedDate ? lease.signedDate.slice(0,10) : '',
      notes: lease.notes || '',
    });
    setEditModal(true);
  };

  const handleUpdate = async () => {
    if(!editForm.monthlyRent) return toast.error(t('apartmentTenantRentRequired'));
    setSaving(true);
    try {
      const payload = editFile ? buildFd(editForm, editFile) : { ...editForm, monthlyRent:Number(editForm.monthlyRent), rentDueDay:Number(editForm.rentDueDay)||1, securityDeposit:Number(editForm.securityDeposit)||0, petDeposit:Number(editForm.petDeposit)||0, utilitiesIncluded:editForm.utilitiesIncluded?editForm.utilitiesIncluded.split(',').map(s=>s.trim()).filter(Boolean):[] };
      await leasesAPI.update(editId, payload);
      toast.success(t('leaseUpdated')); setEditModal(false); setEditId(null); setEditFile(null); load();
    } catch(err){ toast.error(errMsg(err)); }
    finally{ setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await leasesAPI.delete(confirmDel._id);
      toast.success(t('leaseDeleted'));
      setConfirmDel(null); load();
    } catch(err) { toast.error(errMsg(err)); }
  };

  const renew = async id => {
    try { await leasesAPI.renew(id); toast.success(t('leaseRenewed')); load(); }
    catch(err){ toast.error(errMsg(err)); }
  };

  const vacantApts = apts.filter(a=>a.status==='vacant');
  const getProp    = id => props.find(p=>p._id===id);

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">{t('leases')}</h1><p className="page-subtitle">{t('leaseAgreementsRenewals')}</p></div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16}/></button>
          {canCreate && (
            <button className="btn btn-primary" onClick={()=>{setForm(EMPTY);setModal(true)}}><Plus size={16}/>{t('newLease')}</button>
          )}
        </div>
      </div>

      {expiring.length>0&&<div className="alert alert-warning mb-6"><AlertTriangle size={16}/>{expiring.length} {t('leasesExpiringWithin')} 60 {t('days')} — {t('reviewAndRenewalAsNeeded')}</div>}

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div className="search-bar" style={{ flex:1, minWidth:200 }}><Search size={15} color="var(--ink-20)"/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('searchTenant')}/></div>
        <div className="tabs">{['','active','expired','terminated'].map(s=><div key={s} className={`tab${statusF===s?' active':''}`} onClick={()=>setStatusF(s)}>{s?t(s):t('all')}</div>)}</div>
      </div>

      <div className="card">
        <div style={{ overflowX:'auto' }}>
          {loading?<div style={{ padding:40,textAlign:'center' }}><div className="spinner" style={{ margin:'0 auto' }}/></div>:(
            <table className="data-table">
              <thead><tr><th style={{ width:1,whiteSpace:'nowrap' }}></th><th>{t('tenant')}</th><th>{t('propertyUnit')}</th><th>{t('rentPerMonth')}</th><th>{t('start')}</th><th>{t('end')}</th><th>{t('daysLeft')}</th><th>{t('autoRenew')}</th><th>{t('status')}</th></tr></thead>
              <tbody>
                {filtered.map(l=>{
                  const days = daysUntil(l.endDate);
                  const apt  = l.apartmentId;
                  const propName = apt?.propertyId?.name || getProp(apt?.propertyId)?.name;
                  const tenantName = l.tenantId?.name || l.userId?.name || '—';
                  return (
                    <tr key={l._id} style={days!==null&&days<=30&&l.status==='active'?{background:'rgba(245,158,11,0.03)'}:{}}>
                      <td className="table-actions"><div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={()=>setDetail(l)}>{t('view')}</button>
                        {l.leaseFile && <a className="btn btn-secondary btn-sm" href={fileViewUrl(l.leaseFile)} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none' }}><Paperclip size={13}/>File</a>}
                        {canEdit && <button className="btn btn-secondary btn-sm" onClick={()=>openEdit(l)}><Edit2 size={13}/>{t('edit')}</button>}
                        {canEdit && (l.status==='expired'||(days!==null&&days<=30&&l.status==='active'))&&<button className="btn btn-secondary btn-sm" onClick={()=>renew(l._id)}>{t('renew')}</button>}
                        {canDelete && <button className="btn btn-danger btn-sm btn-icon" onClick={()=>setConfirmDel(l)} title={t('delete')}><Trash2 size={13}/></button>}
                      </div></td>
                      <td><div style={{ fontWeight:500 }}>{tenantName}</div></td>
                      <td><div style={{ fontSize:13 }}>{propName||'—'}</div>{apt&&<div style={{ fontSize:11,color:'var(--ink-40)' }}>Unit #{apt.unit}</div>}</td>
                      <td><span style={{ fontWeight:700 }}>{fmt.currency(l.monthlyRent)}</span></td>
                      <td style={{ fontSize:13 }}>{fmt.date(l.startDate)}</td>
                      <td style={{ fontSize:13 }}>{fmt.date(l.endDate)}</td>
                      <td>{days!==null?<span style={{ fontSize:13,fontWeight:600,color:days<0?'var(--ink-20)':days<=30?'var(--rose)':days<=60?'var(--amber)':'var(--ink-60)' }}>{days<0?`${Math.abs(days)}d ago`:days===0?'Today':`${days}d`}</span>:'—'}</td>
                      <td>{l.autoRenew?<span className="badge badge-active">Yes</span>:<span className="badge badge-neutral">No</span>}</td>
                      <td><span className={`badge ${statusClass(l.status)}`}>{statusLabel(l.status)}</span></td>
                    </tr>
                  );
                })}
                {filtered.length===0&&<tr><td colSpan={9} style={{ textAlign:'center',padding:40,color:'var(--ink-40)' }}>{t('noLeasesFound')}</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Lease Modal */}
      {canCreate && (
        <Modal open={modal} onClose={()=>setModal(false)} title={t('newLeaseAgreement')} size="lg"
          footer={<><button className="btn btn-secondary" onClick={()=>setModal(false)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?t('saving'):t('createLease')}</button></>}>
          <LeaseForm form={form} setForm={setForm} vacantApts={vacantApts} tenants={tenants} tenantUsers={tenantUsers} t={t} file={file} fileRef={fileRef} onFileChange={setFile}/>
        </Modal>
      )}

      {/* Edit Lease Modal */}
      {canEdit && (
        <Modal open={editModal} onClose={()=>setEditModal(false)} title="Edit Lease" size="lg"
          footer={<><button className="btn btn-secondary" onClick={()=>setEditModal(false)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>{saving?t('saving'):'Save Changes'}</button></>}>
          <LeaseForm form={editForm} setForm={setEditForm} vacantApts={apts} tenants={tenants} tenantUsers={tenantUsers} t={t} isEdit file={editFile} fileRef={editFileRef} onFileChange={setEditFile} existingFile={leases.find(l=>l._id===editId)?.leaseFile}/>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      <Modal open={!!confirmDel} onClose={()=>setConfirmDel(null)} title="Delete Lease" size="sm"
        footer={<><button className="btn btn-secondary" onClick={()=>setConfirmDel(null)}>{t('cancel')}</button><button className="btn btn-danger" onClick={handleDelete}>{t('delete')}</button></>}>
        {confirmDel && <p style={{ fontSize:14, color:'var(--ink-60)' }}>Delete the lease for <strong>{confirmDel.tenantId?.name || confirmDel.userId?.name}</strong>? If the lease is active, the unit will be marked vacant.</p>}
      </Modal>

      {/* Detail Modal */}
      {detail&&(
        <Modal open={!!detail} onClose={()=>setDetail(null)} title={t('leaseDetails')} size="lg"
          footer={<button className="btn btn-secondary" onClick={()=>setDetail(null)}>{t('close')}</button>}>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <span className={`badge ${statusClass(detail.status)}`}>{statusLabel(detail.status)}</span>
              {detail.autoRenew&&<span className="badge badge-active">Auto-Renew</span>}
              {detail.parkingIncluded&&<span className="badge badge-info">Parking</span>}
            </div>
            <div className="form-grid form-grid-2" style={{ gap:10 }}>
              {[['Tenant',detail.tenantId?.name || detail.userId?.name],['Unit','#'+detail.apartmentId?.unit],['Monthly Rent',fmt.currency(detail.monthlyRent)],['Rent Due Day',detail.rentDueDay||1],['Security Deposit',fmt.currency(detail.securityDeposit)],['Pet Deposit',fmt.currency(detail.petDeposit||0)],['Type',detail.leaseType],['Start',fmt.date(detail.startDate)],['End',fmt.date(detail.endDate)],['Signed',fmt.date(detail.signedDate)],['Utilities',(detail.utilitiesIncluded||[]).join(', ')||'None']].map(([k,v])=>(
                <div key={k} style={{ background:'var(--surface-2)',borderRadius:8,padding:'10px 13px' }}>
                  <div style={{ fontSize:10,color:'var(--ink-40)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2 }}>{k}</div>
                  <div style={{ fontSize:14,fontWeight:600 }}>{v||'—'}</div>
                </div>
              ))}
            </div>
            {detail.notes && (
              <div style={{ background:'var(--surface-2)',borderRadius:8,padding:'10px 13px' }}>
                <div style={{ fontSize:10,color:'var(--ink-40)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2 }}>Notes</div>
                <div style={{ fontSize:14 }}>{detail.notes}</div>
              </div>
            )}
            {detail.leaseFile && (
              <a href={fileViewUrl(detail.leaseFile)} target="_blank" rel="noopener noreferrer"
                className="btn btn-secondary" style={{ textDecoration:'none', alignSelf:'flex-start' }}>
                <FileText size={15}/> View Lease Document
              </a>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function LeaseForm({ form, setForm, vacantApts, tenants, tenantUsers, t, isEdit, file, fileRef, onFileChange, existingFile }) {
  return (
    <div className="form-grid form-grid-2" style={{ gap:16 }}>
      {!isEdit && (
        <>
          <div className="form-group"><label className="form-label">{t('tenant')} *</label><select className="form-input form-select" value={form.tenantId} onChange={e=>setForm(f=>({...f,tenantId:e.target.value}))}><option value="">{t('select')}</option><optgroup label="Tenant Users">{tenantUsers.map(u=><option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}</optgroup><optgroup label="Traditional Tenants">{tenants.map(t=><option key={t._id} value={t._id}>{t.name}</option>)}</optgroup></select></div>
          <div className="form-group"><label className="form-label">{t('vacantUnit')} *</label><select className="form-input form-select" value={form.apartmentId} onChange={e=>{const apt=vacantApts.find(a=>a._id===e.target.value);setForm(f=>({...f,apartmentId:e.target.value,...(apt?.rent?{monthlyRent:String(apt.rent)}:{})}));}}><option value="">{t('select')}</option>{vacantApts.map(a=><option key={a._id} value={a._id}>{a.propertyId?.name||''} – {t('unit')} #{a.unit}</option>)}</select></div>
        </>
      )}
      {isEdit && (
        <div className="form-group"><label className="form-label">Status</label><select className="form-input form-select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>{['active','expired','terminated','pending'].map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}</select></div>
      )}
      <div className="form-group"><label className="form-label">{t('startDate')}</label><input className="form-input" type="date" value={form.startDate} onChange={e=>{const sd=e.target.value;const updates={startDate:sd,signedDate:sd};if(sd){const d=new Date(sd+'T00:00:00');d.setFullYear(d.getFullYear()+1);updates.endDate=d.toISOString().slice(0,10);}setForm(f=>({...f,...updates}));}}/></div>
      <div className="form-group"><label className="form-label">{t('endDate')}</label><input className="form-input" type="date" value={form.endDate} onChange={e=>setForm(f=>({...f,endDate:e.target.value}))}/></div>
      <div className="form-group"><label className="form-label">{t('monthlyRentDollar')} *</label><input className="form-input" type="number" value={form.monthlyRent} onChange={e=>setForm(f=>({...f,monthlyRent:e.target.value}))} placeholder="1500"/></div>
      <div className="form-group">
        <label className="form-label">{t('rentDueDay')}</label>
        <input className="form-input" type="number" min={1} max={31} value={form.rentDueDay}
          onChange={e=>setForm(f=>({...f,rentDueDay:e.target.value}))} placeholder="1"/>
        <div style={{ fontSize:11, color:'var(--ink-40)', marginTop:4 }}>{t('rentDueDayHelp')}</div>
      </div>
      <div className="form-group"><label className="form-label">{t('securityDepositDollar')}</label><input className="form-input" type="number" value={form.securityDeposit} onChange={e=>setForm(f=>({...f,securityDeposit:e.target.value}))} placeholder="3000"/></div>
      <div className="form-group"><label className="form-label">{t('petDepositDollar')}</label><input className="form-input" type="number" value={form.petDeposit} onChange={e=>setForm(f=>({...f,petDeposit:e.target.value}))} placeholder="0"/></div>
      <div className="form-group"><label className="form-label">{t('leaseType')}</label><select className="form-input form-select" value={form.leaseType} onChange={e=>setForm(f=>({...f,leaseType:e.target.value}))}>{['Annual','Month-to-Month','6 Month'].map(lt=><option key={lt}>{lt}</option>)}</select></div>
      <div className="form-group"><label className="form-label">{t('signedDate')}</label><input className="form-input" type="date" value={form.signedDate} onChange={e=>setForm(f=>({...f,signedDate:e.target.value}))}/></div>
      <div className="form-group"><label className="form-label">{t('utilities')}</label><input className="form-input" value={form.utilitiesIncluded} onChange={e=>setForm(f=>({...f,utilitiesIncluded:e.target.value}))} placeholder="Water, Trash, Gas"/></div>
      {isEdit && (
        <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Optional notes…"/></div>
      )}
      <div style={{ gridColumn:'1/-1', display:'flex', gap:16, flexWrap:'wrap' }}>
        <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:14,padding:'6px 0',minHeight:44 }}><input type="checkbox" style={{ width:18,height:18 }} checked={form.autoRenew} onChange={e=>setForm(f=>({...f,autoRenew:e.target.checked}))}/> {t('autoRenew')}</label>
        <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:14,padding:'6px 0',minHeight:44 }}><input type="checkbox" style={{ width:18,height:18 }} checked={form.parkingIncluded} onChange={e=>setForm(f=>({...f,parkingIncluded:e.target.checked}))}/> {t('parkingIncluded')}</label>
      </div>

      {/* Lease file attachment */}
      <div className="form-group" style={{ gridColumn:'1/-1' }}>
        <label className="form-label"><Paperclip size={13}/> Lease Document (PDF / Word / Image)</label>
        {existingFile && !file && (
          <div style={{ fontSize:12, color:'var(--emerald)', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
            <FileText size={13}/> Current file attached —
            <a href={fileViewUrl(existingFile)} target="_blank" rel="noopener noreferrer" style={{ color:'var(--brand)' }}>View</a>
            <span style={{ color:'var(--ink-40)' }}> (upload a new file to replace)</span>
          </div>
        )}
        <div
          style={{ border:'2px dashed var(--border)', borderRadius:8, padding:'14px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:12, background: file ? 'var(--surface-2)' : 'transparent' }}
          onClick={() => fileRef?.current?.click()}
        >
          <input ref={fileRef} type="file" style={{ display:'none' }} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={e => onFileChange(e.target.files[0] || null)}/>
          <Paperclip size={18} color="var(--ink-20)"/>
          {file ? (
            <div>
              <div style={{ fontSize:13, fontWeight:600 }}>{file.name}</div>
              <div style={{ fontSize:11, color:'var(--ink-40)' }}>{(file.size/1024).toFixed(1)} KB</div>
            </div>
          ) : (
            <span style={{ fontSize:13, color:'var(--ink-40)' }}>Click to attach lease agreement…</span>
          )}
          {file && (
            <button type="button" style={{ marginLeft:'auto', fontSize:11, color:'var(--rose)', background:'none', border:'none', cursor:'pointer' }}
              onClick={e => { e.stopPropagation(); onFileChange(null); }}>✕</button>
          )}
        </div>
      </div>
    </div>
  );
}
